import { FC } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Spinner from '$components/Spinner/Spinner';
import StatusCandidatePosition from '$types/serverTypes/StatusCandidatePosition';
import {
  CandidatePositionWithHistory,
  useGetCandidatePositionHistoryQuery,
} from '$store/modules/candidate-position-status';

enum PositionApplicationOrigin {
  internalRostering = 'internalRostering',
  applied = 'applied',
  unknown = 'unknown',
}

/**
 * How the candidate got into the position, read from the status the placement
 * was created with: internal users roster a candidate in as `new`, candidates
 * applying start as `interested`. A backfilled initial status only tells us
 * what the placement looked like when change tracking started, so it cannot
 * answer the question.
 */
const resolveOrigin = (
  position: CandidatePositionWithHistory
): PositionApplicationOrigin => {
  if (position.IsInitialStatusApproximate) {
    return PositionApplicationOrigin.unknown;
  }

  switch (position.InitialStatus) {
    case StatusCandidatePosition.new:
      return PositionApplicationOrigin.internalRostering;
    case StatusCandidatePosition.interested:
      return PositionApplicationOrigin.applied;
    default:
      return PositionApplicationOrigin.unknown;
  }
};

// `terminated` and `removed` are valid history statuses but are not part of the
// front-end StatusCandidatePosition enum, so they are keyed as plain strings.
const STATUS_BADGE_STYLES: { [key: string]: string } = {
  [StatusCandidatePosition.new]: 'bg-[#CFD0FF] text-[#2C30D7]',
  [StatusCandidatePosition.interested]: 'bg-[#CFD0FF] text-[#2C30D7]',
  [StatusCandidatePosition.shortlisted]: 'bg-[#D9E7FF] text-[#1B4FA8]',
  [StatusCandidatePosition.requestInterview]: 'bg-[#FCEFC4] text-[#8A6100]',
  [StatusCandidatePosition.offerSent]: 'bg-[#FCDAC4] text-[#A44101]',
  [StatusCandidatePosition.hired]: 'bg-[#BFEFEC] text-[#016F68]',
  [StatusCandidatePosition.declined]: 'bg-[#FFD6D6] text-[#A00000]',
  terminated: 'bg-[#FFD6D6] text-[#A00000]',
  removed: 'bg-gray-200 text-gray-600',
};

const ORIGIN_BADGE_STYLES: { [key in PositionApplicationOrigin]: string } = {
  [PositionApplicationOrigin.internalRostering]:
    'bg-internal-main/20 text-internal-main',
  [PositionApplicationOrigin.applied]: 'bg-[#BFEFEC] text-[#016F68]',
  [PositionApplicationOrigin.unknown]: 'bg-gray-200 text-gray-600',
};

const formatDate = (value: string) => dayjs(value).format('MMM D, YYYY h:mm A');

const PositionCard: FC<{ position: CandidatePositionWithHistory }> = ({
  position,
}) => {
  const [t] = useTranslation('global');

  const statusLabel = (status: string) =>
    t(`CandidatePositionsTab.Status.${status}`, { defaultValue: status });

  const origin = resolveOrigin(position);

  return (
    <div className="flex flex-col gap-3 rounded border border-solid border-gray-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col text-brand-darkgrey">
          <Link
            className="text-sm font-bold text-internal-main hover:underline"
            to={`/simera-dashboard/positions/${position.PositionId}`}
          >
            {position.PositionName || `#${position.PositionId}`}
          </Link>
          {position.CompanyName && (
            <span className="text-xs">{position.CompanyName}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`text-[10px] font-bold py-1 px-2 rounded-md whitespace-nowrap ${ORIGIN_BADGE_STYLES[origin]}`}
          >
            {t(`CandidatePositionsTab.Origin.${origin}`)}
          </span>
          <span
            className={`text-[10px] font-bold py-1 px-2 rounded-md whitespace-nowrap ${
              STATUS_BADGE_STYLES[position.CurrentStatus] ??
              'bg-gray-200 text-gray-600'
            }`}
          >
            {statusLabel(position.CurrentStatus)}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        <span>
          <span className="font-bold">
            {t('CandidatePositionsTab.AddedOn')}:
          </span>{' '}
          {formatDate(position.AddedAt)}
        </span>
        {position.StartDate && (
          <span>
            <span className="font-bold">
              {t('CandidatePositionsTab.StartDate')}:
            </span>{' '}
            {formatDate(position.StartDate)}
          </span>
        )}
        {position.EndDate && (
          <span>
            <span className="font-bold">
              {t('CandidatePositionsTab.EndDate')}:
            </span>{' '}
            {formatDate(position.EndDate)}
          </span>
        )}
        {position.RemovedAt && (
          <span>
            <span className="font-bold">
              {t('CandidatePositionsTab.RemovedOn')}:
            </span>{' '}
            {formatDate(position.RemovedAt)}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-bold text-brand-darkgrey">
          {t('CandidatePositionsTab.StatusHistory')}
        </span>
        {position.History.length === 0 ? (
          <span className="text-xs italic text-gray-500">
            {t('CandidatePositionsTab.NoHistory')}
          </span>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left text-gray-600">
              <thead className="text-gray-500">
                <tr>
                  <th className="font-bold py-1 pr-4">
                    {t('CandidatePositionsTab.StatusHeader')}
                  </th>
                  <th className="font-bold py-1 pr-4">
                    {t('CandidatePositionsTab.From')}
                  </th>
                  <th className="font-bold py-1 pr-4">
                    {t('CandidatePositionsTab.Date')}
                  </th>
                  <th className="font-bold py-1">
                    {t('CandidatePositionsTab.ChangedBy')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {position.History.map((entry) => (
                  <tr
                    className="border-t border-solid border-gray-100"
                    key={entry.Id}
                  >
                    <td className="py-1 pr-4 whitespace-nowrap">
                      <span
                        className={`text-[10px] font-bold py-1 px-2 rounded-md ${
                          STATUS_BADGE_STYLES[entry.ToStatus] ??
                          'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {statusLabel(entry.ToStatus)}
                      </span>
                    </td>
                    <td className="py-1 pr-4 whitespace-nowrap">
                      {entry.FromStatus ? statusLabel(entry.FromStatus) : '—'}
                    </td>
                    <td className="py-1 pr-4 whitespace-nowrap">
                      {formatDate(entry.ChangedAt)}
                      {entry.IsBackfilled && (
                        <span className="ml-2 italic text-gray-400">
                          ({t('CandidatePositionsTab.Approximate')})
                        </span>
                      )}
                    </td>
                    <td className="py-1 whitespace-nowrap">
                      {entry.ChangedBy || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const PositionsTab: FC<{ candidateId: number }> = ({ candidateId }) => {
  const [t] = useTranslation('global');

  const { data: positions, isLoading } = useGetCandidatePositionHistoryQuery({
    candidateId,
  });

  return (
    <section className="bg-white p-8 rounded">
      <p className="text-xl font-semibold text-brand-darkgrey mb-4">
        {t('CandidatePositionsTab.Title')}
      </p>
      {isLoading && <Spinner secondary height="100%" width="100%" />}
      {!isLoading && !positions?.length && (
        <span className="text-sm italic text-gray-500">
          {t('CandidatePositionsTab.Empty')}
        </span>
      )}
      <div className="flex flex-col gap-4">
        {positions?.map((position) => (
          <PositionCard key={position.Id} position={position} />
        ))}
      </div>
    </section>
  );
};

export default PositionsTab;
